import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { messageAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Archive, 
  ArchiveRestore,
  Mail,
  Bell,
  Clock,
  Tag,
  FileText
} from 'lucide-react';

export function MessageDetailPage() {
  const { messageId } = useParams();
  const navigate = useNavigate();
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMessage();
  }, [messageId]);

  const loadMessage = async () => {
    try {
      const response = await messageAPI.getMessage(messageId);
      setMessage(response.data);
    } catch (error) {
      toast.error('Failed to load message');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async () => {
    try {
      await messageAPI.updateMessage(messageId, { archived: !message.archived });
      setMessage({ ...message, archived: !message.archived });
      toast.success(message.archived ? 'Message restored' : 'Message archived');
    } catch (error) {
      toast.error('Failed to update message');
    }
  };

  const renderContent = () => {
    if (!message?.content) return null;

    switch (message.content_type) {
      case 'markdown':
        return (
          <div className="message-content prose prose-slate dark:prose-invert max-w-none">
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
            >
              {message.content}
            </ReactMarkdown>
          </div>
        );
      case 'html':
        return (
          <div 
            className="message-content prose prose-slate dark:prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: message.content }} 
          />
        );
      default:
        return (
          <div className="whitespace-pre-wrap font-sans text-foreground leading-relaxed">
            {message.content}
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="p-8 space-y-6" data-testid="message-detail-loading">
        <Skeleton className="h-10 w-32" />
        <Skeleton className="h-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!message) {
    return (
      <div className="p-8 text-center">
        <p className="text-muted-foreground">Message not found</p>
        <Button onClick={() => navigate(-1)} className="mt-4">
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto space-y-6" data-testid="message-detail-page">
      {/* Back Button */}
      <Button 
        variant="ghost" 
        onClick={() => navigate(-1)}
        data-testid="back-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back
      </Button>

      {/* Message Header */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                message.type === 'alert' 
                  ? 'bg-accent/10' 
                  : 'bg-primary/10'
              }`}>
                {message.type === 'alert' ? (
                  <Bell className="w-6 h-6 text-accent" />
                ) : (
                  <Mail className="w-6 h-6 text-primary" />
                )}
              </div>
              <div>
                <CardTitle className="font-heading text-2xl mb-2">{message.subject}</CardTitle>
                <div className="flex flex-wrap items-center gap-3">
                  <Badge variant={message.type === 'alert' ? 'default' : 'secondary'} className={
                    message.type === 'alert' ? 'bg-accent text-accent-foreground' : ''
                  }>
                    {message.type === 'alert' ? 'Alert' : 'Inbox'}
                  </Badge>
                  {message.archived && (
                    <Badge variant="outline">Archived</Badge>
                  )}
                  <Badge variant="outline" className="capitalize">
                    <FileText className="w-3 h-3 mr-1" />
                    {message.content_type}
                  </Badge>
                </div>
              </div>
            </div>
            <Button 
              variant="outline"
              onClick={handleArchive}
              data-testid="archive-message-btn"
            >
              {message.archived ? (
                <>
                  <ArchiveRestore className="w-4 h-4 mr-2" />
                  Restore
                </>
              ) : (
                <>
                  <Archive className="w-4 h-4 mr-2" />
                  Archive
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <Separator />
        <CardContent className="pt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground mb-1 flex items-center gap-1">
                <Tag className="w-3 h-3" /> Source
              </p>
              <p className="font-medium capitalize">{message.source}</p>
            </div>
            <div>
              <p className="text-muted-foreground mb-1 flex items-center gap-1">
                <Clock className="w-3 h-3" /> Sent
              </p>
              <p className="font-medium">{formatDate(message.sent_date)}</p>
            </div>
            {message.viewed_at && (
              <div>
                <p className="text-muted-foreground mb-1 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Viewed
                </p>
                <p className="font-medium">{formatDate(message.viewed_at)}</p>
              </div>
            )}
            {message.expiry_date && (
              <div>
                <p className="text-muted-foreground mb-1 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Expires
                </p>
                <p className="font-medium">{formatDate(message.expiry_date)}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Message Content */}
      <Card data-testid="message-content">
        <CardContent className="p-6">
          {renderContent()}
        </CardContent>
      </Card>
    </div>
  );
}
